import { zodResolver } from '@hookform/resolvers/zod'
import { DEFAULT_FOLLOW_UP_PHASES, UpdateTenantTrainingConfigSchema } from '@lms/shared'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect } from 'react'
import { type Control, useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'
import { NotificationChannelSelectItems } from '@/components/training/NotificationChannelSelectItems'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  useTenantTrainingConfig,
  useUpdateTenantTrainingConfig,
} from '@/hooks/use-tenant-training-config'

type ConfigFormValues = z.infer<typeof UpdateTenantTrainingConfigSchema>

type PhaseNumberFieldName = 'dueAfterMonths' | 'dueAfterDays' | 'openDaysBeforeDue'

function PhaseNumberField({
  control,
  index,
  name,
  label,
  unit,
  max,
}: {
  control: Control<ConfigFormValues>
  index: number
  name: PhaseNumberFieldName
  label: string
  unit: string
  max: number
}) {
  return (
    <FormField
      control={control}
      name={`followUpPhases.${index}.${name}`}
      render={({ field }) => (
        <FormItem>
          {index === 0 && <FormLabel>{label}</FormLabel>}
          <div className="flex items-center gap-1">
            <FormControl>
              <Input
                type="number"
                min={0}
                max={max}
                className="w-16"
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            </FormControl>
            <span className="text-sm text-muted-foreground">{unit}</span>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export default function TenantTrainingConfigPage() {
  const { data, isLoading } = useTenantTrainingConfig()
  const updateConfig = useUpdateTenantTrainingConfig()

  const form = useForm<ConfigFormValues>({
    resolver: zodResolver(UpdateTenantTrainingConfigSchema),
    defaultValues: {
      defaultNotificationChannel: undefined,
      defaultHrContactEmail: '',
      teamsWebhookUrl: '',
      followUpPhases: [...DEFAULT_FOLLOW_UP_PHASES],
      approvalRequiredForOpen: true,
      approvalTimeoutDays: 7,
      reminderEnabled: true,
      reminderSendHour: 9,
    },
  })

  const followUpPhases = useFieldArray({ control: form.control, name: 'followUpPhases' })

  useEffect(() => {
    if (data) {
      const cfg = data as any
      form.reset({
        defaultNotificationChannel: cfg.defaultNotificationChannel ?? undefined,
        defaultHrContactEmail: cfg.defaultHrContactEmail ?? '',
        teamsWebhookUrl: cfg.teamsWebhookUrl ?? '',
        followUpPhases: cfg.followUpPhases ?? [...DEFAULT_FOLLOW_UP_PHASES],
        approvalRequiredForOpen: cfg.approvalRequiredForOpen ?? true,
        approvalTimeoutDays: cfg.approvalTimeoutDays ?? 7,
        reminderEnabled: cfg.reminderEnabled ?? true,
        reminderSendHour: cfg.reminderSendHour ?? 9,
      })
    }
  }, [data, form])

  const onSubmit = async (values: ConfigFormValues) => {
    try {
      await updateConfig.mutateAsync(values)
      toast.success('設定を保存しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }

  const appendPhase = () =>
    followUpPhases.append({ label: '', dueAfterMonths: 3, dueAfterDays: 0, openDaysBeforeDue: 14 })

  const phasesError = form.formState.errors.followUpPhases
  const phasesErrorMessage = phasesError?.root?.message ?? phasesError?.message

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <h1 className="text-2xl font-bold">テナント研修設定</h1>
        <Skeleton className="h-96" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold">テナント研修設定</h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">通知設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="defaultNotificationChannel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>デフォルト通知チャネル</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === 'none' ? undefined : v)}
                      defaultValue={field.value ?? 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="選択" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <NotificationChannelSelectItems />
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="teamsWebhookUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teams 通知の Webhook URL</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://...api.powerplatform.com/powerautomate/automations/direct/workflows/..."
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Power Automate の「Teams Webhook 要求を受信したとき」トリガーの URL
                      です。未設定の場合、Teams 通知は送信されません。
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="defaultHrContactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>デフォルト問い合わせメール</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="hr@example.com"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      承認期限超過のエスカレーション通知の送信先になります
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">フォローアップ（スキルチェック）設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormDescription>
                各回の期日は、1回目は研修の最終日、2回目以降は前の回の期日を起点に「◯か月◯日後」で設定します。回答は期日の◯日前から行えます。期日（当日を含む）を過ぎても提出できます。下の「リマインダーを有効にする」がオンの場合、未提出の評価者には毎日リマインドが届きます。
              </FormDescription>

              {followUpPhases.fields.map((phaseField, index) => (
                <div
                  key={phaseField.id}
                  className="grid grid-cols-[1fr_auto_auto_auto_auto] items-start gap-2"
                >
                  <FormField
                    control={form.control}
                    name={`followUpPhases.${index}.label`}
                    render={({ field }) => (
                      <FormItem>
                        {index === 0 && <FormLabel>名称</FormLabel>}
                        <FormControl>
                          <Input placeholder="受講3か月後" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <PhaseNumberField
                    control={form.control}
                    index={index}
                    name="dueAfterMonths"
                    label="期日"
                    unit="か月"
                    max={24}
                  />
                  <PhaseNumberField
                    control={form.control}
                    index={index}
                    name="dueAfterDays"
                    label="&nbsp;"
                    unit="日後"
                    max={90}
                  />
                  <PhaseNumberField
                    control={form.control}
                    index={index}
                    name="openDaysBeforeDue"
                    label="回答開始"
                    unit="日前から"
                    max={90}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={index === 0 ? 'mt-8' : undefined}
                    onClick={() => followUpPhases.remove(index)}
                    disabled={followUpPhases.fields.length === 1}
                    aria-label={`第${index + 1}回を削除`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {phasesErrorMessage && (
                <p className="text-sm font-medium text-destructive">{phasesErrorMessage}</p>
              )}

              <Button type="button" variant="outline" size="sm" onClick={appendPhase}>
                <Plus className="mr-1 h-4 w-4" />
                回を追加
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">承認設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="approvalRequiredForOpen"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0">募集型研修に承認を必須にする</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="approvalTimeoutDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>承認期限（日数）</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">リマインダー設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="reminderEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0">リマインダーを有効にする</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reminderSendHour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>送信時刻（0〜23時）</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Button type="submit" className="w-full" disabled={updateConfig.isPending}>
            {updateConfig.isPending ? '保存中...' : '設定を保存'}
          </Button>
        </form>
      </Form>
    </div>
  )
}
